/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useRef } from 'react'
import ProgressBar from 'progressbar.js'

interface CircularProgressProps {
  progress: number // 0-100
  size?: number
  strokeWidth?: number
  trailWidth?: number
  color?: string
  trailColor?: string
  showText?: boolean
}

/**
 * CircularProgress Component
 *
 * Wraps progressbar.js library to create circular progress indicators.
 * Matches the legacy AngularJS progressbar component functionality.
 */
export const CircularProgress: React.FC<CircularProgressProps> = ({
  progress,
  size = 35,
  strokeWidth = 8,
  trailWidth = 8,
  color = '#4a90e2',
  trailColor = '#e0e0e0',
  showText = true
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const progressBarRef = useRef<ProgressBar.Circle | null>(null)

  useEffect(() => {
    if (!containerRef.current) return

    // Initialize progress bar
    progressBarRef.current = new ProgressBar.Circle(containerRef.current, {
      color: color,
      strokeWidth: strokeWidth,
      trailWidth: trailWidth,
      trailColor: trailColor,
      easing: 'easeInOut',
      duration: 300,
      svgStyle: {
        display: 'block',
        width: '100%'
      },
      text: {
        value: showText ? '0%' : '',
        alignToBottom: false,
        style: {
          position: 'absolute',
          left: '50%',
          top: '50%',
          padding: 0,
          margin: 0,
          transform: {
            prefix: true,
            value: 'translate(-50%, -50%)'
          },
          color: '#333',
          fontSize: '11px',
          fontWeight: 'bold'
        }
      },
      step: function (state: any, circle: any) {
        if (showText) {
          const value = Math.round(circle.value() * 100)
          circle.setText(value + '%')
        }
      }
    })

    return () => {
      if (progressBarRef.current) {
        progressBarRef.current.destroy()
        progressBarRef.current = null
      }
    }
  }, [color, strokeWidth, trailWidth, trailColor, showText])

  useEffect(() => {
    if (progressBarRef.current) {
      const normalizedProgress = Math.max(0, Math.min(100, progress)) / 100
      progressBarRef.current.animate(normalizedProgress)
    }
  }, [progress])

  return (
    <div
      ref={containerRef}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        position: 'relative'
      }}
    />
  )
}

export default CircularProgress
